import { Button } from '@/components/ui/button';
import { formatMoney } from '@/lib/money';
import { useTranslation } from '@/i18n';
import type { LoyaltyRedemption } from '@/api/types';

interface LoyaltyRedeemedProps {
    result: LoyaltyRedemption;
    currency: string;
    locale: string;
    onContinue: () => void;
}

/**
 * "You saved $X. Your new total is $Y."
 *
 * Both numbers matter and they are different kinds of thing: the saving is what the guest just earned, and
 * the total is what they are about to be asked for. The total comes from the POS rather than from arithmetic
 * here — see `LoyaltyRedemption`.
 */
export function LoyaltyRedeemed({ result, currency, locale, onContinue }: LoyaltyRedeemedProps) {
    const { t } = useTranslation();
    const money = (minorUnits: number) => formatMoney(minorUnits, currency, locale);

    return (
        <div className="flex flex-col gap-6">
            {/* `status` rather than `alert`: good news the guest caused and is looking at. */}
            <div role="status" className="flex flex-col gap-2 text-center">
                <h1 className="text-2xl font-semibold tracking-tight text-balance">
                    {t('loyalty.saved', { amount: money(result.discountCents) })}
                </h1>
                <p className="tabular text-lg font-medium text-muted-foreground">
                    {t('loyalty.newTotal', { amount: money(result.newTotalCents) })}
                </p>
            </div>

            <Button size="lg" onClick={onContinue}>
                {t('loyalty.continue')}
            </Button>
        </div>
    );
}
